import React, { useRef } from 'react';
import { useIntl } from 'react-intl';
import { PiCaretUp } from 'react-icons/pi';
import { CHIP_MENU_ALIGN_RIGHT, CHIP_MENU_PLACEMENT_TOP, ChipMenu } from '@aibuild/ui';
import { formatSpacing } from '../format';
import { GRID_SPACINGS } from '../viewerSettings';
import { usePortalContainer } from '../environment';
import PillTrigger from './PillTrigger';
import { messages } from './messages';
import { usePopover } from './usePopover';
import { GridSlot } from './GridMenu.styled';

export interface GridMenuProps {
  /** The spacing drawn now, in millimetres. */
  spacing: number;
  /** The spacing chosen, or null to follow the zoom. */
  fixed: number | null;
  onChange: (spacing: number | null) => void;
}

const GridMenu: React.FC<GridMenuProps> = ({ spacing, fixed, onChange }) => {
  const intl = useIntl();
  const portal = usePortalContainer();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menu = usePopover(triggerRef);
  const menuLabel = intl.formatMessage(messages.gridMenu);
  const pick = (next: number | null) => {
    onChange(next);
    menu.close();
  };
  return (
    <GridSlot>
      <PillTrigger
        ref={triggerRef}
        tone="readout"
        label={intl.formatMessage(messages.grid, { spacing: formatSpacing(intl, spacing) })}
        ariaLabel={menuLabel}
        caret={<PiCaretUp />}
        open={menu.open}
        onClick={menu.toggle}
        onMouseDown={menu.keepOpenOnPress}
        testId="model-viewer__grid"
      />
      {menu.open && (
        <ChipMenu
          onClose={menu.close}
          portal
          portalContainer={portal}
          anchorRef={triggerRef}
          align={CHIP_MENU_ALIGN_RIGHT}
          placement={CHIP_MENU_PLACEMENT_TOP}
          width={160}
          ariaLabel={menuLabel}
          dataTestId="model-viewer__grid-menu"
        >
          <ChipMenu.List>
            <ChipMenu.Item isActive={fixed === null} onClick={() => pick(null)} dataTestId="model-viewer__grid-follow">
              {intl.formatMessage(messages.gridFollow)}
            </ChipMenu.Item>
            {GRID_SPACINGS.map((option) => (
              <ChipMenu.Item
                key={option}
                isActive={fixed === option}
                onClick={() => pick(option)}
                dataTestId={`model-viewer__grid-${option}`}
              >
                {formatSpacing(intl, option)}
              </ChipMenu.Item>
            ))}
          </ChipMenu.List>
        </ChipMenu>
      )}
    </GridSlot>
  );
};

export default GridMenu;
